<template>
  <div v-show="show" ref="popup" class="menu-popup">
    <div ref="arrowRef" class="menu-popup-arrow" data-popper-arrow></div>
    <slot :forceUpdate="() => popper?.update()"></slot>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, onUpdated, ref, watch } from "vue";
import { createPopper } from "@popperjs/core/lib/popper-lite";
import type { Instance as PopperInstance } from "@popperjs/core/lib/popper-lite";
import type { Placement } from "@popperjs/core/lib/enums";
import flip from "@popperjs/core/lib/modifiers/flip";
import arrow from "@popperjs/core/lib/modifiers/arrow";
import offset from "@popperjs/core/lib/modifiers/offset";
import preventOverflow from "@popperjs/core/lib/modifiers/preventOverflow";

// TODO: add Transition, this probably requires KeepAlive to avoid glitches on v-leave

const props = defineProps<{
  reference: Element | null;
  placement: Placement;
  offset: number;
  show: boolean;
}>();

const popup = ref<HTMLElement | null>(null);
const popper = ref<PopperInstance | null>(null);

defineExpose({ popper });

const options = () => ({
  placement: props.placement ?? "auto",
  modifiers: [
    preventOverflow,
    {
      name: "preventOverflow",
    },
    flip,
    offset,
    {
      name: "offset",
      options: {
        offset: [0, props.offset ?? 0],
      },
    },
    arrow,
    {
      name: "arrow",
      options: {},
    },
  ],
});

// Always recreate popper when mounting/unmounting component
onMounted(() => {
  // console.group("onMounted");
  if (props.reference != null && popup.value != null) {
    // console.log("create popper");
    popper.value = createPopper(props.reference, popup.value, options());
  }
  // console.groupEnd();
});
onBeforeUnmount(() => {
  // console.group("onBeforeUnmount");
  // if (popper.value != null) console.log("destroy popper");
  popper.value?.destroy();
  // console.groupEnd();
});
onUpdated(() => {
  popper.value?.update();
});

// Re-attach popper when reference is changed dynamically
watch(
  () => props.reference,
  (newReference) => {
    // console.group("watch reference");
    // if (popper.value != null) console.log("destroy popper");
    popper.value?.destroy();
    if (newReference != null && popup.value != null) {
      // console.log("create popper");
      popper.value = createPopper(newReference, popup.value, options());
    }
    // console.groupEnd();
  }
);

// Manage state when showing/hiding
watch(
  () => props.show,
  (newShow) => {
    const resolvedShow = newShow ?? true;

    // Enable/disable the event listeners (https://popper.js.org/docs/v2/tutorial/#performance)
    popper.value?.setOptions((opts) => ({
      ...opts,
      modifiers: [
        ...(opts.modifiers || []),
        { name: "eventListeners", enabled: resolvedShow },
      ],
    }));

    if (resolvedShow) popper.value?.update();
  }
);

// Update options on change
watch(
  () => [props.offset, props.placement],
  () => {
    popper.value?.setOptions(options());
  }
);
</script>

<style>
.menu-popup {
  @apply bg-base-100 shadow-md shadow-base-content/10 rounded-box z-40 border border-neutral/60;
}

.menu-popup-arrow,
.menu-popup-arrow::before {
  @apply absolute box-border bg-inherit border-inherit;
  --arrow: 10px;
  --half-arrow-neg: calc(-0.5 * var(--arrow));
  width: calc(var(--arrow) - 1px);
  height: calc(var(--arrow) - 1px);
}

.menu-popup-arrow {
  @apply invisible;
}

.menu-popup-arrow::before {
  @apply visible rotate-45 content-[""];
}

.menu-popup[data-popper-placement^="top"] > .menu-popup-arrow {
  bottom: var(--half-arrow-neg);
}
.menu-popup[data-popper-placement^="top"] > .menu-popup-arrow:before {
  @apply border-r border-b;
}

.menu-popup[data-popper-placement^="bottom"] > .menu-popup-arrow {
  top: var(--half-arrow-neg);
}
.menu-popup[data-popper-placement^="bottom"] > .menu-popup-arrow:before {
  @apply border-l border-t;
}

.menu-popup[data-popper-placement^="left"] > .menu-popup-arrow {
  right: var(--half-arrow-neg);
}
.menu-popup[data-popper-placement^="left"] > .menu-popup-arrow:before {
  @apply border-r border-t;
}

.menu-popup[data-popper-placement^="right"] > .menu-popup-arrow {
  left: var(--half-arrow-neg);
}
.menu-popup[data-popper-placement^="right"] > .menu-popup-arrow:before {
  @apply border-l border-b;
}

.v-enter-active,
.v-leave-active {
  @apply transition-opacity duration-200;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
